/* eslint-disable react-refresh/only-export-components */
/**
 * Authentication Context Provider for Supabase + Tekion validation
 *
 * Token refresh is handled exclusively through the backend /auth/refresh endpoint
 * (not Supabase native autoRefreshToken, which is disabled to prevent race conditions).
 * A proactive timer fires 60 seconds before the access token expires so the user
 * never experiences a failed API request due to an expired token.
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import type { User, Session } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { buildApiUrl, getErrorMessage } from '../utils/authUtils';
import { ensureFreshSession } from '../services/api';
import apiService from '../services/apiService';
import { ADMIN } from '../services/apiConstant';
import posthog from 'posthog-js';

interface UserProfile {
  id: number | null;
  email: string;
  display_name: string;
  access_level: string;
  dealer_ids: string[];
  role_id: number | null;
  role_source: string | null;
  permissions: Record<string, string>;
}

const ACCESS_ORDER: Record<string, number> = { none: 0, view: 1, edit: 2 };
// Referentially stable fallback so `permissions` doesn't mint a new object
// identity every render while loading/erroring — a fresh `{}` literal there
// would defeat useCallback's memoization of `can` below.
const EMPTY_PERMISSIONS: Record<string, string> = {};

export interface AuthContextType {
  user: User | null;
  session: Session | null;
  dealerIds: string[];
  displayName: string;
  accessLevel: string | null;
  roleId: number | null;
  permissions: Record<string, string> | null;
  permissionsLoading: boolean;
  loading: boolean;
  can: (key: string, minimum: 'view' | 'edit') => boolean;
  signUp: (email: string, password: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [dealerIds, setDealerIds] = useState<string[]>([]);
  const [displayName, setDisplayName] = useState<string>('');
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queryClient = useQueryClient();

  const {
    data: profile,
    isPending: usersMeIsPending,
  } = useQuery({
    queryKey: ['users-me', user?.id],
    queryFn: () => apiService.get<UserProfile>(ADMIN.me),
    enabled: !!user,
    staleTime: 60_000,
    retry: false,
  });

  // isPending (not isLoading) — isLoading is isPending && isFetching, which
  // reads false for a render or two right after `enabled` flips true but
  // before fetching has actually started, letting callers see "not loading"
  // with no data yet. isPending stays true until data genuinely arrives.
  const permissionsLoading = !!user && usersMeIsPending;
  const accessLevel = profile?.access_level ?? null;
  const roleId = profile?.role_id ?? null;
  const permissions = profile?.permissions ?? (user ? EMPTY_PERMISSIONS : null);

  const can = useCallback(
    (key: string, minimum: 'view' | 'edit'): boolean => {
      const level = permissions?.[key] ?? 'none';
      return (ACCESS_ORDER[level] ?? 0) >= (ACCESS_ORDER[minimum] ?? 0);
    },
    [permissions]
  );

  // Extract custom claims from session
  const extractCustomClaims = useCallback((currentSession: Session | null) => {
    if (!currentSession?.user) {
      setDealerIds([]);
      setDisplayName('');
      return;
    }

    const appMetadata = currentSession.user.app_metadata || {};
    const userDealerIds = appMetadata.dealer_ids || [];
    const userDisplayName = appMetadata.display_name || '';

    setDealerIds(userDealerIds);
    setDisplayName(userDisplayName);

    // Identify user in PostHog
    if (posthog && currentSession.user.id) {
      posthog.identify(currentSession.user.id, {
        email: currentSession.user.email,
        display_name: userDisplayName,
        dealer_ids: userDealerIds,
        user_created_at: currentSession.user.created_at,
      });
    }
  }, []);

  // Schedule a proactive token refresh 60 seconds before the access token expires.
  // autoRefreshToken is disabled, so we must do this ourselves to prevent sessions
  // from expiring mid-use and causing 401s.
  const scheduleProactiveRefresh = useCallback((currentSession: Session | null) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (!currentSession?.expires_at) return;

    const expiresAtMs = currentSession.expires_at * 1000;
    const refreshAtMs = expiresAtMs - 60 * 1000; // 60 seconds before expiry
    const delayMs = refreshAtMs - Date.now();

    if (delayMs <= 0) {
      // Already expired or within the 60-second window — refresh now
      ensureFreshSession().catch(async () => {
        await supabase.auth.signOut();
        window.location.href = '/login';
      });
      return;
    }

    refreshTimerRef.current = setTimeout(async () => {
      try {
        await ensureFreshSession();
      } catch {
        await supabase.auth.signOut();
        window.location.href = '/login';
      }
    }, delayMs);
  }, []);

  // Let Supabase handle session recovery from storage on load.
  // Listen to auth state changes to keep React state in sync and reschedule the refresh timer.
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      extractCustomClaims(initialSession);
      scheduleProactiveRefresh(initialSession);
      setLoading(false);
    });

    // Listen for auth changes (sign in, sign out, token refresh)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, currentSession) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      extractCustomClaims(currentSession);
      scheduleProactiveRefresh(currentSession);
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [extractCustomClaims, scheduleProactiveRefresh]);

  // Sign up with email and password
  const signUp = async (email: string, password: string): Promise<void> => {
    try {
      const backendUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8002';

      const response = await fetch(buildApiUrl(backendUrl, '/auth/signup'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(getErrorMessage(data, 'Sign up failed'));
      }

      toast.success('Account created successfully! You can now log in.');
    } catch (error) {
      console.warn('Sign up error:', error);
      throw (error as Error);
    }
  };

  // Sign in with email and password
  const signIn = async (email: string, password: string): Promise<void> => {
    try {
      const backendUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8002';

      const response = await fetch(buildApiUrl(backendUrl, '/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(getErrorMessage(data, 'Login failed'));
      }

      // Set session with tokens from backend
      const { error } = await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });

      if (error) throw error;

      // Extract dealer IDs from response
      setDealerIds(data.dealer_ids || []);
      setDisplayName(data.display_name || '');

      toast.success(`Welcome back, ${data.display_name || email}!`);
    } catch (error) {
      console.warn('Sign in error:', error);
      throw (error as Error);
    }
  };

  // Sign out
  const signOut = async (): Promise<void> => {
    try {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Clear custom claims
      setDealerIds([]);
      setDisplayName('');

      // Drop the cached /users/me profile so a subsequent sign-in as a
      // different user never briefly shows the previous user's permissions.
      queryClient.removeQueries({ queryKey: ['users-me'] });

      // Same reasoning for per-user preferences (e.g. default report views) —
      // a different user signing in on this device shouldn't see stale
      // cached defaults belonging to whoever was signed in before.
      queryClient.removeQueries({ queryKey: ['user-preference'] });

      // Reset PostHog user identification
      if (posthog) {
        posthog.reset();
      }

      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error('Failed to log out');
      throw (error as Error);
    }
  };

  // Manually refresh session via the backend (used by external callers if needed)
  const refreshSession = async (): Promise<void> => {
    try {
      await ensureFreshSession();
    } catch (error) {
      console.error('Refresh session error:', error);
      toast.error('Session refresh failed. Please log in again.');
      await signOut();
    }
  };

  const value: AuthContextType = {
    user,
    session,
    dealerIds,
    displayName,
    accessLevel,
    roleId,
    permissions,
    permissionsLoading,
    loading,
    can,
    signUp,
    signIn,
    signOut,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
